import React from "react";
import "../styles/process-visualization.css";
import "../styles/shared-ui.css";
import "../styles/gateway-section.css";
import "../styles/bpmn-editor.css";
import { getProcessInformation } from "../services/processService";
import {
  clearStoredBpmnXml,
  createGateway,
  getBpmnTasks,
  getBpmnPng,
  loadBpmnXml,
  loadProcessForEditor,
  saveBpmnXml,
  updateGateway,
  validateProcessFlow,
} from "../services/bpmnService";
import BpmnEditor from "../components/ui/BpmnEditor";
import GatewayConfigModal from "../components/ui/GatewayConfigModal";
import SearchableDropdown from "../components/ui/SearchableDropdown";

const formatProcessOptionLabel = (process) => {
  const processName =
    process?.process_name || process?.name || "Unnamed process";
  const processVersion = process?.process_version ?? process?.version ?? 0;
  const companyName = process?.company?.name || "";

  return `${processName} (V${processVersion})| ${companyName}`;
};

/**
 * BPMN Editor Page
 * Features:
 * - Process selection with search
 * - Static image preview with Edit button
 * - Visual bpmn-js editor for drag-and-drop editing
 * - Save changes back to backend
 * - Zoom and pan controls
 */
const BpmnViewerPage = () => {
  // Process selection state
  const [processes, setProcesses] = React.useState([]);
  const [labelToId, setLabelToId] = React.useState(new Map());
  const [selectedLabel, setSelectedLabel] = React.useState("");
  const [selectedId, setSelectedId] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  // Preview state
  const [imageUrl, setImageUrl] = React.useState("");
  const imageUrlRef = React.useRef("");
  const [previewLoading, setPreviewLoading] = React.useState(false);

  // Process and BPMN data
  const [processData, setProcessData] = React.useState(null);
  const [bpmnXml, setBpmnXml] = React.useState("");
  // Last XML this session actually saved, per process id. The backend may
  // normalize/re-layout XML it echoes back on refetch, so re-entering edit
  // mode prefers what we know was just saved over a fresh server fetch —
  // otherwise the user's arrangement can appear to shift/break (BUG-04).
  const lastSavedXmlRef = React.useRef({});
  const [gateways, setGateways] = React.useState([]);
  const [processTasks, setProcessTasks] = React.useState([]);
  const [, setLoadingTasks] = React.useState(false);
  const [showGatewayModal, setShowGatewayModal] = React.useState(false);
  const [editingGateway, setEditingGateway] = React.useState(null);

  // Editor state
  const [isEditing, setIsEditing] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [saveMessage, setSaveMessage] = React.useState(null);
  // Breadcrumb of parent processes when drilling into Call Activities.
  const [drillStack, setDrillStack] = React.useState([]);

  // Validation state
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [validationWarnings, setValidationWarnings] = React.useState([]);

  // Zoom and pan state for static preview
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const dragRef = React.useRef({
    active: false,
    startX: 0,
    startY: 0,
    baseX: 0,
    baseY: 0,
  });

  // Auto-save debounce timer
  const autoSaveTimerRef = React.useRef(null);
  // Keep a ref to selectedId so the debounced callback always sees the latest value
  const selectedIdRef = React.useRef(selectedId);
  React.useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const revokeUrl = React.useCallback(() => {
    const current = imageUrlRef.current;
    if (current) {
      try {
        URL.revokeObjectURL(current);
      } catch (_) {}
    }
    imageUrlRef.current = "";
    setImageUrl("");
    setBpmnXml("");
  }, []);

  // Load process list on mount
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await getProcessInformation();

        if (!mounted) return;
        const arr = Array.isArray(list) ? list : [];
        setProcesses(arr);
        const map = new Map();
        arr.forEach((p) => {
          const label = formatProcessOptionLabel(p);
          const id = p.process_id || p.id;
          map.set(label, id);
        });
        setLabelToId(map);
      } catch (e) {
        if (mounted) {
          setProcesses([]);
          setLabelToId(new Map());
        }
        console.error("Failed to load processes for BPMN", e);
      }
    })();
    return () => {
      mounted = false;
      revokeUrl();
    };
  }, [revokeUrl]);

  const processOptions = React.useMemo(
    () =>
      Array.from(labelToId.entries()).map(([label, id]) => ({
        label,
        value: String(id),
      })),
    [labelToId],
  );

  // Reverse lookup (process id -> label) for drill-down navigation.
  const idToLabel = React.useMemo(() => {
    const map = new Map();
    labelToId.forEach((id, label) => {
      map.set(id, label);
      map.set(Number(id), label);
    });
    return map;
  }, [labelToId]);

  const getSelectedId = (label) => {
    const v = String(label || "").trim();
    if (!v) return null;
    if (labelToId.has(v)) return labelToId.get(v);
    const found = processes.find((p) => String(p.process_id || p.id) === v);
    return found ? found.process_id || found.id : null;
  };

  const onProcessChange = async (nextLabel) => {
    setSelectedLabel(nextLabel);
    const pid = getSelectedId(nextLabel);
    setSelectedId(pid);
    // Reset gateways and tasks when process changes
    setGateways([]);
    setProcessTasks([]);
    // Load tasks for the selected process
    if (pid) {
      setLoadingTasks(true);
      try {
        const tasksData = await getBpmnTasks(pid);
        // getBpmnTasks already normalizes to an array (tasks + subprocesses)
        setProcessTasks(Array.isArray(tasksData) ? tasksData : []);
      } catch (e) {
        console.error("Failed to load process tasks:", e);
        setProcessTasks([]);
      } finally {
        setLoadingTasks(false);
      }
    }
  };

  const handleSaveGateways = React.useCallback(async (nextGateways) => {
    const gatewayList = Array.isArray(nextGateways) ? nextGateways : [];
    if (!selectedId) {
      setGateways(gatewayList);
      return;
    }

    try {
      const savedGateways = [];
      for (const gateway of gatewayList) {
        const numericId = Number(gateway?.id);
        const hasPersistedId = Number.isFinite(numericId) && numericId > 0;
        const saved =
          hasPersistedId ?
            await updateGateway(numericId, gateway)
          : await createGateway(selectedId, gateway);
        savedGateways.push(saved || gateway);
      }
      const reloaded = await loadProcessForEditor(selectedId).catch(() => null);
      if (reloaded?.gateways) {
        setGateways(reloaded.gateways);
        setProcessData(reloaded);
      } else {
        setGateways(savedGateways);
      }
      setSaveMessage({ type: "success", text: "Gateway configuration saved." });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error("Failed to save gateways:", error);
      setSaveMessage({
        type: "error",
        text: error?.message || "Failed to save gateway configuration",
      });
      throw error;
    }
  }, [selectedId]);

  // Load process data for editor
  const loadProcessData = async (processId) => {
    try {
      // Load process with relations and saved BPMN XML in parallel
      const [data, xml] = await Promise.all([
        loadProcessForEditor(processId),
        loadBpmnXml(processId).catch(() => null), // Saved XML might not be available
      ]);

      setProcessData(data);
      if (xml) setBpmnXml(xml);

      return data;
    } catch (error) {
      console.error("Failed to load process data:", error);
      throw error;
    }
  };

  // Load and display the PNG preview for an explicit process id.
  const loadAndShow = async (pid) => {
    if (!pid) return;
    setLoading(true);
    revokeUrl();
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setIsEditing(false);
    setValidationErrors([]);
    setValidationWarnings([]);
    try {
      // Load process data first
      await loadProcessData(pid);

      // Generate initial PNG preview (skip validation for editing/preview)
      const url = await getBpmnPng(pid, { skipValidation: true });
      imageUrlRef.current = url;
      setImageUrl(url);
    } catch (e) {
      alert(e?.message || "Failed to generate BPMN");
    } finally {
      setLoading(false);
    }
  };

  // Load and display BPMN preview for the currently selected process.
  const generateBpmn = () => loadAndShow(selectedId);

  // BPMN 2.0 drill-down: open a Call Activity's referenced child process as its
  // own diagram, remembering the parent so the user can navigate back.
  const drillIntoProcess = async (childId, childName) => {
    if (!childId) return;
    if (
      hasUnsavedChanges &&
      !window.confirm(
        "You have unsaved changes. Discard them and open the sub-process?",
      )
    ) {
      return;
    }
    const childLabel = idToLabel.get(childId) || childName || String(childId);
    setDrillStack((stack) => [
      ...stack,
      { id: selectedId, label: selectedLabel },
    ]);
    setSelectedId(childId);
    setSelectedLabel(childLabel);
    setHasUnsavedChanges(false);
    await loadAndShow(childId);
  };

  // Navigate back to the parent process after drilling into a sub-process.
  const goBackToParent = async () => {
    if (drillStack.length === 0) return;
    const parent = drillStack[drillStack.length - 1];
    setDrillStack((stack) => stack.slice(0, -1));
    setSelectedId(parent.id);
    setSelectedLabel(parent.label);
    setHasUnsavedChanges(false);
    await loadAndShow(parent.id);
  };

  // Enter edit mode with bpmn-js visual editor
  const enterEditMode = async () => {
    if (!selectedId) return;

    setPreviewLoading(true);
    try {
      // Prefer the XML we know was just saved this session over a fresh
      // server fetch, which may come back re-laid-out (see lastSavedXmlRef).
      const xml =
        lastSavedXmlRef.current[selectedId] ||
        (await loadBpmnXml(selectedId));
      setBpmnXml(xml);
      setIsEditing(true);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Failed to load BPMN XML:", error);
      alert(
        "Failed to load BPMN for editing. The editor will start with the current diagram.",
      );
      setIsEditing(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Exit edit mode
  const exitEditMode = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm("You have unsaved changes. Discard them?")) {
        return;
      }
    }
    // Cancel any pending auto-save
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    setIsEditing(false);
    setHasUnsavedChanges(false);
    setValidationErrors([]);
    setValidationWarnings([]);
  };

  // Handle XML changes from the visual editor.
  // NOTE: We intentionally do NOT call setBpmnXml(newXml) here.
  // The bpmn-js modeler already holds the updated XML internally.
  // Feeding it back via the xml prop would trigger a re-import in
  // BpmnEditor's useEffect which resets hasChanges to false,
  // preventing the Save button from ever becoming enabled.
  //
  // Auto-save: every visual change (add/move/delete/reorder) is
  // debounced and persisted via PUT /bpmn-editor/process/:id/save-xml.
  // The backend syncs gateways, branches, and task ordering automatically.
  const handleXmlChange = React.useCallback((newXml) => {
    setHasUnsavedChanges(true);

    if (!newXml) return;

    // Clear any pending auto-save timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(async () => {
      const pid = selectedIdRef.current;
      if (!pid || !newXml) return;
      try {
        await saveBpmnXml(pid, newXml);
        lastSavedXmlRef.current[pid] = newXml;
        setHasUnsavedChanges(false);
        setSaveMessage({
          type: "success",
          text: "Changes saved automatically.",
        });
        setTimeout(() => setSaveMessage(null), 2000);
      } catch (err) {
        console.error("Auto-save failed:", err);
        // Non-blocking: user can still manually save via the Save button
      }
    }, 1000);
  }, []);

  // Save from visual editor using stored XML endpoint.
  const handleEditorSave = async (xml) => {
    if (!selectedId) return;

    setSaveMessage(null);
    try {
      await saveBpmnXml(selectedId, xml);
      lastSavedXmlRef.current[selectedId] = xml;

      setHasUnsavedChanges(false);
      setSaveMessage({ type: "success", text: "Diagram saved successfully!" });

      // Refresh the static preview image from the newly-saved XML
      try {
        const url = await getBpmnPng(selectedId, { skipValidation: true });
        imageUrlRef.current = url;
        setImageUrl(url);
      } catch (_) {
        /* preview refresh is non-critical */
      }

      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error("Failed to save:", error);
      setSaveMessage({
        type: "error",
        text: error?.message || "Failed to save diagram",
      });
    }
  };

  const handleResetLayout = async () => {
    if (!selectedId) return;
    const confirmed = window.confirm(
      "Reset layout to auto-generated diagram? This clears stored XML.",
    );
    if (!confirmed) return;

    setSaveMessage(null);
    setPreviewLoading(true);
    try {
      await clearStoredBpmnXml(selectedId);
      delete lastSavedXmlRef.current[selectedId];

      const regeneratedXml = await loadBpmnXml(selectedId, {
        skipValidation: true,
        regenerate: true,
      });
      setBpmnXml(regeneratedXml);

      const url = await getBpmnPng(selectedId, {
        skipValidation: true,
        regenerate: true,
      });
      imageUrlRef.current = url;
      setImageUrl(url);

      setHasUnsavedChanges(false);
      setSaveMessage({
        type: "success",
        text: "Layout reset to regenerated diagram.",
      });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error("Failed to reset layout:", error);
      setSaveMessage({
        type: "error",
        text: error?.message || "Failed to reset layout",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Validate the diagram
  const handleValidate = async () => {
    if (!selectedId) return;

    try {
      const result = await validateProcessFlow(selectedId);
      setValidationErrors(result?.errors || []);
      setValidationWarnings(result?.warnings || []);

      if (!result?.errors?.length && !result?.warnings?.length) {
        setSaveMessage({
          type: "success",
          text: "Diagram is valid! No issues found.",
        });
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (error) {
      console.error("Validation failed:", error);
      // If validation endpoint not available, show a message
      setSaveMessage({
        type: "info",
        text: "Validation completed (basic check)",
      });
      setTimeout(() => setSaveMessage(null), 2000);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">BPMN Editor</h2>
            <p className="page-subtitle">
              View and edit BPMN diagrams with visual drag-and-drop editing
            </p>
            {drillStack.length > 0 && (
              <button
                onClick={goBackToParent}
                className="secondary-btn"
                style={{
                  marginTop: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontWeight: 600,
                }}
                title="Return to the parent process"
              >
                ← Back to{" "}
                {drillStack[drillStack.length - 1]?.label || "parent process"}
              </button>
            )}
          </div>
          {selectedId && imageUrl && !isEditing && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                className="secondary-btn"
                onClick={handleResetLayout}
                disabled={previewLoading}
                style={{ fontWeight: 600 }}
                title="Clear stored XML and regenerate diagram layout"
              >
                Reset Layout
              </button>
              <button
                className="primary-btn"
                onClick={enterEditMode}
                disabled={previewLoading}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontWeight: 600,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit Diagram
              </button>
            </div>
          )}
        </div>
      </div>

      {saveMessage && (
        <div
          style={{
            margin: "0 24px 12px",
            padding: "12px 16px",
            borderRadius: 8,
            background:
              saveMessage.type === "success" ? "#d1fae5"
              : saveMessage.type === "error" ? "#fee2e2"
              : "#e0f2fe",
            color:
              saveMessage.type === "success" ? "#065f46"
              : saveMessage.type === "error" ? "#991b1b"
              : "#0369a1",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {saveMessage.type === "success" ?
            "✓"
          : saveMessage.type === "error" ?
            "⚠"
          : "ℹ"}{" "}
          {saveMessage.text}
        </div>
      )}

      <div className="page-content whatif">
        {/* Process Selection */}
        <div className="role-card">
          <div className="section-title">Select Process</div>
          <div className="form-grid">
            <div className="form-group">
              <label>Process</label>
              <SearchableDropdown
                id="bpmn-process-search-select"
                className="input searchable-combobox"
                value={selectedLabel}
                options={processOptions}
                placeholder="Search process by name/company"
                clearOnFocus
                disabled={isEditing}
                onChange={async (label, option) => {
                  if (!label) {
                    setSelectedLabel("");
                    setSelectedId(null);
                    setProcessData(null);
                    setHasUnsavedChanges(false);
                    setIsEditing(false);
                    setValidationErrors([]);
                    setValidationWarnings([]);
                    revokeUrl();
                    return;
                  }
                  await onProcessChange(option?.label ?? label);
                }}
              />
            </div>
            <div className="form-group" style={{ alignSelf: "end" }}>
              <button
                className="primary-btn"
                onClick={generateBpmn}
                disabled={!selectedId || loading || isEditing}
              >
                {loading ? "Loading…" : "Load Process"}
              </button>
            </div>
          </div>
        </div>

        {/* Visual BPMN Editor - shown when editing */}
        {isEditing && (
          <div
            className="role-card"
            style={{ marginTop: 12, padding: 0, overflow: "hidden" }}
          >
            <BpmnEditor
              xml={bpmnXml}
              onXmlChange={handleXmlChange}
              onSave={handleEditorSave}
              onValidate={handleValidate}
              onCancel={exitEditMode}
              processName={processData?.processName || ""}
              height="calc(100vh - 240px)"
              validationErrors={validationErrors}
              validationWarnings={validationWarnings}
              onOpenCalledProcess={drillIntoProcess}
            />
          </div>
        )}

        {/* Static BPMN Preview - shown when not editing */}
        {!isEditing && imageUrl && (
          <div className="role-card" style={{ marginTop: 12 }}>
            <div
              className="section-title"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <span>
                BPMN Preview
                {previewLoading && (
                  <span style={{ marginLeft: 8, color: "#6b7280" }}>
                    (Loading...)
                  </span>
                )}
              </span>
              {processData && (
                <span
                  style={{ fontSize: 14, fontWeight: 400, color: "#6b7280" }}
                >
                  {processData.processName} ({processData.processCode})
                </span>
              )}
            </div>
            <div
              className="po-slider"
              onMouseMove={(e) => {
                const d = dragRef.current;
                if (!d.active) return;
                const dx = e.clientX - d.startX;
                const dy = e.clientY - d.startY;
                setOffset({ x: d.baseX + dx, y: d.baseY + dy });
              }}
              onMouseUp={() => {
                dragRef.current = {
                  active: false,
                  startX: 0,
                  startY: 0,
                  baseX: 0,
                  baseY: 0,
                };
              }}
              onMouseLeave={() => {
                dragRef.current = {
                  active: false,
                  startX: 0,
                  startY: 0,
                  baseX: 0,
                  baseY: 0,
                };
              }}
              // Touch mirrors the mouse drag above: one finger pans the
              // zoomed-in image (touch-action on the wrap stops the page from
              // scrolling meanwhile).
              onTouchMove={(e) => {
                const d = dragRef.current;
                if (!d.active || e.touches.length !== 1) return;
                const t = e.touches[0];
                setOffset({
                  x: d.baseX + (t.clientX - d.startX),
                  y: d.baseY + (t.clientY - d.startY),
                });
              }}
              onTouchEnd={() => {
                dragRef.current = {
                  active: false,
                  startX: 0,
                  startY: 0,
                  baseX: 0,
                  baseY: 0,
                };
              }}
            >
              <div
                className="po-slides"
                style={{ height: "calc(100vh - 320px)", minHeight: 380 }}
              >
                <div
                  className="po-zoom-wrap"
                  onMouseDown={(e) => {
                    if (zoom <= 1) return;
                    dragRef.current = {
                      active: true,
                      startX: e.clientX,
                      startY: e.clientY,
                      baseX: offset.x,
                      baseY: offset.y,
                    };
                  }}
                  onTouchStart={(e) => {
                    if (zoom <= 1 || e.touches.length !== 1) return;
                    const t = e.touches[0];
                    dragRef.current = {
                      active: true,
                      startX: t.clientX,
                      startY: t.clientY,
                      baseX: offset.x,
                      baseY: offset.y,
                    };
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    cursor: zoom > 1 ? "grab" : "default",
                    touchAction: zoom > 1 ? "none" : "auto",
                  }}
                >
                  <img
                    src={imageUrl}
                    alt="BPMN Diagram"
                    className="po-slide"
                    style={{
                      transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                      transformOrigin: "center center",
                    }}
                  />
                </div>
              </div>
              <div className="po-zoom-controls">
                <button
                  type="button"
                  onClick={() =>
                    setZoom((z) => {
                      const next = Math.max(0.1, Math.min(3, z - 0.15));
                      if (next <= 0.15) setOffset({ x: 0, y: 0 });
                      return next;
                    })
                  }
                  title="Zoom Out"
                  aria-label="Zoom Out"
                >
                  −
                </button>
                <span className="po-zoom-level">{Math.round(zoom * 100)}%</span>
                <button
                  type="button"
                  onClick={() =>
                    setZoom((z) => Math.max(0.1, Math.min(3, z + 0.15)))
                  }
                  title="Zoom In"
                  aria-label="Zoom In"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setZoom(1);
                    setOffset({ x: 0, y: 0 });
                  }}
                  title="Reset"
                  aria-label="Reset"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Gateway Configuration Modal */}
      <GatewayConfigModal
        open={showGatewayModal}
        onClose={() => {
          setShowGatewayModal(false);
          setEditingGateway(null);
        }}
        onSave={handleSaveGateways}
        tasks={processTasks}
        initialGateways={editingGateway ? [] : gateways}
        editGateway={editingGateway}
        wizardMode={!!editingGateway}
      />
    </div>
  );
};

export default BpmnViewerPage;
